import React, { useState } from 'react';
import '../../styles/MobileScreener.css';

/**
 * Visual strategy card for basic screener mode
 * Displays preset strategy with all parameters clearly visible
 *
 * @param {string} strategyKey - Unique identifier for the strategy
 * @param {string} title - Strategy title (e.g., "Conservative Bull", "Market Hedge")
 * @param {string} subtitle - Strategy description
 * @param {string} expiration - Expiration timeframe (e.g., "90 days")
 * @param {string} strike - Strike position (e.g., "ITM -20%", "ATM")
 * @param {string} daysToTarget - Days to exit (e.g., "45 days")
 * @param {string} expectedMove - Expected stock movement (e.g., "+10%", "-10%")
 * @param {string} optionType - Option type ("call" or "put")
 * @param {boolean} isSelected - Whether this strategy is currently selected
 * @param {function} onClick - Click handler for selection
 */
const StrategyCard = ({
  strategyKey,
  title,
  subtitle,
  expiration,
  strike,
  daysToTarget,
  expectedMove,
  optionType,
  isSelected = false,
  onClick
}) => {
  const isCall = optionType === 'call';
  const [showInfo, setShowInfo] = useState(false);

  // Parameter descriptions
  const parameterInfo = {
    expiration: "Time until the option contract expires. Longer expirations cost more but give more time for your thesis to play out.",
    strike: "The price at which you can buy (call) or sell (put) the underlying stock. ITM = In The Money (profitable now), OTM = Out of The Money (needs stock movement).",
    daysToTarget: "When you plan to close the position, as a percentage of time to expiration. Exit before expiration to avoid time decay.",
    underlyingScenario: "The expected stock price movement. Positive for bullish strategies, negative for bearish strategies."
  };

  const handleInfoClick = (e) => {
    e.stopPropagation(); // Prevent card selection when clicking info
    setShowInfo(!showInfo);
  };

  return (
    <button
      type="button"
      onClick={() => onClick(strategyKey)}
      className={`strategy-card ${isSelected ? 'selected' : ''} ${isCall ? 'call-strategy' : 'put-strategy'} ${showInfo ? 'show-info' : ''}`}
      aria-pressed={isSelected}
    >
      <div className="strategy-card-header">
        <h4 className="strategy-card-title">{title}</h4>
        <div className="header-actions">
          <div
            className="info-button"
            onClick={handleInfoClick}
            onKeyDown={(e) => e.key === 'Enter' && handleInfoClick(e)}
            role="button"
            tabIndex={0}
            aria-label="Show parameter information"
            title="Click for parameter descriptions"
          >
            ⓘ
          </div>
          <span className={`option-type-badge ${isCall ? 'call' : 'put'}`}>
            {isCall ? 'CALL' : 'PUT'}
          </span>
        </div>
      </div>

      <p className="strategy-card-subtitle">{subtitle}</p>

      {/* Info overlay - shown when info button is clicked */}
      {showInfo && (
        <div className="strategy-info-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="info-content">
            <div
              className="info-close"
              onClick={handleInfoClick}
              onKeyDown={(e) => e.key === 'Enter' && handleInfoClick(e)}
              role="button"
              tabIndex={0}
              aria-label="Close information"
            >
              ✕
            </div>
            <h5 className="info-title">Parameter Descriptions</h5>
            <div className="info-item">
              <strong>Expiration:</strong>
              <p>{parameterInfo.expiration}</p>
            </div>
            <div className="info-item">
              <strong>Strike:</strong>
              <p>{parameterInfo.strike}</p>
            </div>
            <div className="info-item">
              <strong>Days to Exit:</strong>
              <p>{parameterInfo.daysToTarget}</p>
            </div>
            <div className="info-item">
              <strong>Underlying Scenario:</strong>
              <p>{parameterInfo.underlyingScenario}</p>
            </div>
          </div>
        </div>
      )}

      {/* Clear parameter grid */}
      <div className="strategy-params-grid">
        <div className="param-item">
          <span className="param-label">Expiration</span>
          <span className="param-value">{expiration}</span>
        </div>
        <div className="param-item">
          <span className="param-label">Strike</span>
          <span className="param-value">{strike}</span>
        </div>
        <div className="param-item">
          <span className="param-label">Days to Exit</span>
          <span className="param-value">{daysToTarget}</span>
        </div>
        <div className="param-item">
          <span className="param-label">Underlying Scenario</span>
          <span className="param-value expected-move">{expectedMove}</span>
        </div>
      </div>
    </button>
  );
};

export default StrategyCard;
